import * as React from 'react';
import {useHistory} from 'react-router-dom';

import adminRoutes from 'routes/admin.routes';
import NotAuthorized from 'components/notAuthorized';
import {useFindQuery} from 'features/appnames/appnamesApi';
import {TableActions} from 'components/tableActions';

import {
  Box,
  Button,
  Cluster,
  Cover,
  Heading,
  ITableCol,
  Notification,
  Paragraph,
  Pill,
  Table,
  TdLink,
  Template,
} from '@pluto-tv/assemble';

import {IAppName} from 'models/appNames';
import {useAppPermissions} from 'app/permissions';
import {withThousandsSeparator} from 'utils/thousands-separator';
import {getPrefixedUrl} from 'routes';

const TdPills = ({row}) =>
  row && row.devices && row.devices.length > 0 ? (
    <Cluster space='xsmall'>
      {row.devices.map(device => (
        <Pill key={device} label={device}></Pill>
      ))}
    </Cluster>
  ) : (
    <>No devices</>
  );

const ROWS_PER_PAGE = 25;

export default (): JSX.Element => {
  const history = useHistory();
  const [page, setPage] = React.useState(0);
  const [allAppNames, setAllAppNames] = React.useState<IAppName[]>([]);
  const [lazyLoading, setLazyLoading] = React.useState<boolean>(false);
  const {ableTo, permissions} = useAppPermissions();

  if (!ableTo('APPNAME_VIEW')) {
    return <NotAuthorized />;
  }

  const {data: appNames, isFetching} = useFindQuery(
    {
      offset: page * ROWS_PER_PAGE,
      limit: ROWS_PER_PAGE,
      sort: 'createdAt:desc',
    },
    {refetchOnMountOrArgChange: true},
  );

  const handleCreate = () => {
    history.push(adminRoutes.paths.appNameEditPage.replace(':id', 'new'));
  };

  const handleEdit = (role: IAppName) => {
    history.push(adminRoutes.paths.appNameEditPage.replace(':id', role._id));
  };

  React.useEffect(() => {
    setLazyLoading(isFetching);
  }, [isFetching]);

  React.useEffect(() => {
    if (appNames?.data) {
      setAllAppNames(prevAppNames => {
        const uniqueAppNames = page === 0 ? [] : [...prevAppNames];
        appNames.data.forEach(newAppName => {
          if (!uniqueAppNames.some(existing => existing._id === newAppName._id)) {
            uniqueAppNames.push(newAppName);
          }
        });
        return uniqueAppNames;
      });
    }
  }, [appNames?.data, page]);

  const onLazyLoad = React.useCallback(() => {
    const hasReachedEnd = appNames?.data && appNames.data.length < ROWS_PER_PAGE;

    if (!isFetching && !hasReachedEnd && allAppNames.length < (appNames?.metadata.totalCount || 0)) {
      setPage(prevPage => prevPage + 1);
    }
  }, [isFetching, allAppNames.length, appNames?.metadata.totalCount, appNames?.data]);

  return (
    <>
      <Cover scrolling={true} gutter='large' coverTemplateHeight='100%' padding={{mobile: 'medium', wide: 'large'}}>
        <Template label='header'>
          <Cluster justify='space-between' align='center' space='medium'>
            <Cluster align='end' space='small'>
              <Heading level='h1'>App Profile Management</Heading>
              <Paragraph color='secondary'>
                {withThousandsSeparator(appNames?.metadata.totalCount || 0)} Items
              </Paragraph>
            </Cluster>
            <Button type='primary' onClick={() => handleCreate()} permission={permissions.APPNAME_CREATE}>
              + New App Name
            </Button>
          </Cluster>
        </Template>
        <Template label='cover'>
          <Box
            background='pewter'
            borderTop={true}
            borderSize='0.125rem'
            borderColor='cavern'
            paddingTop={(allAppNames?.length as number) > 0 ? 'xsmall' : 'medium'}
            paddingBottom='none'
            paddingX='large'
            fullHeight={true}
          >
            <Table
              loading={isFetching && page === 0}
              fixedHeader={true}
              wrapContent={true}
              lazyLoading={page !== 0 && lazyLoading}
              onLazyLoad={onLazyLoad}
              lazyLoadingMsg='Loading more items...'
              cols={[
                {
                  label: 'App Name',
                  transform: row => (
                    <TdLink
                      row={row}
                      title={row.name}
                      url={getPrefixedUrl(adminRoutes.paths.appNameEditPage.replace(':id', row._id))}
                      onClick={handleEdit}
                    />
                  ),
                },
                {
                  label: 'Associated Devices',
                  transform: row => <TdPills row={row} />,
                },
                ...(ableTo('APPNAME_EDIT')
                  ? [
                      {
                        label: 'Actions',
                        colWidth: '6.25rem',
                        transform: row => (
                          <TableActions
                            row={row}
                            icons={ableTo('APPNAME_EDIT') ? ['edit'] : []}
                            onClick={(row, icon) => {
                              switch (icon) {
                                case 'edit':
                                  handleEdit(row);
                                  break;
                                default:
                              }
                            }}
                          />
                        ),
                      } as ITableCol<IAppName>,
                    ]
                  : []),
              ]}
              rows={allAppNames}
            >
              <Template label='empty'>
                <Notification type='warning'>There are no App Names currently available.</Notification>
              </Template>
            </Table>
          </Box>
        </Template>
      </Cover>
    </>
  );
};
